/*
 * Copyright Amazon.com, Inc. and its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT
 *
 * Licensed under the MIT License. See the LICENSE accompanying this file
 * for the specific language governing permissions and limitations under
 * the License.
 */

import { Auth } from 'aws-amplify';
import { AuthState } from '@aws-amplify/ui-components';

import { storeTokens, setTokenCookie, setRefreshTokenCookie } from './tokenHelper';
import { eraseCookie } from './cookieHelper';

import awsconfig from '../../../aws-exports';

/**
 * Check whether the user requires MFA setup using Cognito data only (CSP-safe,
 * no cross-origin requests).
 *
 * Requires the SANA backend to sync the per-user "mfa_required" flag to the
 * Cognito custom attribute `custom:mfa_required` (string "true"/"false") via
 * adminUpdateUserAttributes whenever an admin toggles the setting.
 *
 * - custom:mfa_required  → "true" when the admin has enabled MFA for this user
 * - Auth.getPreferredMFA → "NOMFA" when the user has not yet configured MFA
 */
async function isMfaSetupRequired(cognitoUser) {
	try {
		const mfaRequiredAttr = cognitoUser.attributes?.['custom:sana_mfa_required'];
		const mfaRequired = mfaRequiredAttr === 'true' || mfaRequiredAttr === true;
		console.log('mfaRequiredAttr', mfaRequiredAttr);

		if (!mfaRequired) {
			return false;
		}

		/*
		 * MFA is required for this user. Setup is only needed if the user has NOT
		 * already configured a preferred MFA method. Without this check the /mfa
		 * setup screen is re-shown on every sign in even after TOTP was verified,
		 * because custom:sana_mfa_required stays "true" for the life of the setting.
		 * bypassCache forces a server-side read so a freshly confirmed TOTP device
		 * is reflected immediately.
		 */
		const preferredMfa = await Auth.getPreferredMFA(cognitoUser, { bypassCache: true });
		console.log('preferredMfa', preferredMfa);

		return preferredMfa === 'NOMFA';
	} catch (err) {
		console.error('MFA setup check failed:', err);
		return false;
	}
}

const handleAuthUIStateChange = async (authState) => {
	if (authState === AuthState.SignedIn) {
		var redirect_uri;
		var authorization_code;
		var clientState;
		let queryStringParams = new URLSearchParams(window.location.search);
		let qsRedirectUri = queryStringParams.get('redirect_uri');
		let qsAuthorizationCode = queryStringParams.get('authorization_code');
		let qsClientState = queryStringParams.get('state');

		/*
		 * For a local sign in the redirect_uri/authorization_code will be in the query string params
		 */
		if (qsRedirectUri) {
			redirect_uri = qsRedirectUri;
			authorization_code = qsAuthorizationCode;
			clientState = qsClientState;
		} else {
			/*
			 * For a federated sign in the redirect_uri/authorization_code will be in the local storage
			 */
			redirect_uri = localStorage.getItem('client-redirect-uri');
			authorization_code = localStorage.getItem('authorization_code');
			clientState = localStorage.getItem('client-state');
			localStorage.removeItem(`client-redirect-uri`);
			localStorage.removeItem(`authorization_code`);
			localStorage.removeItem(`client-state`);
		}

		/*
		 * get the current user session and the full Cognito user object (needed for
		 * custom attributes and getPreferredMFA)
		 */
		let authInfo = await Auth.currentSession();
		let cognitoUser = await Auth.currentAuthenticatedUser({ bypassCache: true });

		let idToken = authInfo.idToken.jwtToken;
		let accessToken = authInfo.accessToken.jwtToken;
		let refreshToken = authInfo.refreshToken.token;

		/*
		 * Set the ID and access token cookies for fast SSO
		 */
		if (idToken && accessToken && refreshToken) {
			setTokenCookie('id_token', idToken);
			setTokenCookie('access_token', accessToken);

			/*
			 * Set the refresh token cookie. Refresh token cannot be parsed for an expiry
			 * so use the access token expiry. Although the refresh token has a longer expiry,
			 * this is for the purpose of fast SSO — the cookie will be refreshed when the
			 * id/access token cookie expires.
			 */
			setRefreshTokenCookie(refreshToken, accessToken);
		} else {
			console.error('Inconsistent application state: Tokens missing from current session');
			return;
		}

		/*
		 * Before redirecting back to the client app, check if MFA setup is required.
		 * Uses only Cognito APIs — no cross-origin requests, no CSP issues.
		 * If setup is needed, redirect to /mfa with the final destination encoded as
		 * mfa_redirect so the user lands on the right page after completing setup.
		 */
		if (await isMfaSetupRequired(cognitoUser)) {
			let mfaRedirect;
			if (authorization_code && redirect_uri) {
				// PKCE flow: store tokens first so the code stays valid after MFA setup
				const storeResp = await storeTokens(authorization_code, idToken, accessToken, refreshToken);
				if (storeResp.status === 200) {
					// SECURITY: use the server-validated redirect_uri from /storage, not the URL param.
					const validatedRedirectUri = storeResp.data && storeResp.data.redirect_uri;
					mfaRedirect = validatedRedirectUri
						? validatedRedirectUri + '/?code=' + authorization_code + (clientState !== undefined ? '&state=' + clientState : '')
						: awsconfig.ui_url;
				} else if (storeResp.status === 302) {
					mfaRedirect = storeResp.headers.location;
				} else {
					console.error('Could not store tokens before MFA redirect. Server response: ' + storeResp.data);
					mfaRedirect = awsconfig.ui_url;
				}
			} else {
				// Implicit flow (id_token in redirect URL) has been removed for security reasons.
				mfaRedirect = awsconfig.ui_url;
			}
			window.location.replace('/mfa?mfa_required=true&mfa_redirect=' + encodeURIComponent(mfaRedirect));
			return;
		}

		if (authorization_code && redirect_uri) {
			/*
			 * PKCE Flow
			 */
			const response = await storeTokens(authorization_code, idToken, accessToken, refreshToken);

			if (response.status === 200) {
				/*
				 * SECURITY: use the server-validated redirect_uri returned by /storage,
				 * NOT the redirect_uri from the browser URL. This prevents authorization
				 * code hijacking via a spoofed redirect_uri (the SPA no longer trusts the
				 * client-supplied destination).
				 */
				const validatedRedirectUri = response.data && response.data.redirect_uri;
				if (validatedRedirectUri) {
					window.location.replace(validatedRedirectUri + '/?code=' + authorization_code + (clientState !== undefined ? '&state=' + clientState : ''));
				} else {
					console.error('No validated redirect_uri returned from storage');
					window.location.replace(awsconfig.ui_url);
				}
			} else if (response.status === 302) {
				window.location.replace(response.headers.location);
			} else {
				console.error('Could not store tokens. Server response: ' + response.data);
			}
		} else {
			/*
			 * Either a direct sign in to the broker (not part of an oauth2 client flow),
			 * or an unsupported/legacy implicit request (response_type=id_token), which has
			 * been removed for security reasons. In both cases send the user to the UI.
			 */
			window.location.replace(awsconfig.ui_url);
		}
	} else if (authState === AuthState.SignedOut) {
		eraseCookie('id_token');
		eraseCookie('access_token');
		eraseCookie('refresh_token');
	}
};

export default handleAuthUIStateChange;
